<template>
  <ModalDialog
    ref="dialog"
    title="Edit Order Despatch"
    :submitting="submitting"
    submitTitle="Update"
    icon="fa-truck"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <div v-if="holdProcessLoading">
      <p class="text-center">Placing a hold on the order while editing</p>
      <Spinner />
    </div>

    <FormGroup v-bind:form="dialogForm" :labelColumnSize="3" v-show="!holdProcessLoading">
      <NotificationBar type="warning" v-if="isDeliveryItemInvoiced">
        <NotificationBarTitle>The delivery order item has been invoiced and will not be updated</NotificationBarTitle>
      </NotificationBar>

      <FormRow label="Despatch">
        <FormOption label="Delivery" name="despatchType" :setting="DespatchTypes.delivery" v-model="order.despatchType" inline="true" />
        <FormOption label="Pickup" name="despatchType" :setting="DespatchTypes.pickup" v-model="order.despatchType" inline="true" />
      </FormRow>
      <template v-if="isDespatchDelivery">
        <div class="form-group row">
          <label class="col-sm-3 col-form-label">Delivery Fee</label>
          <div class="col-sm-5">
            <DeliveryStockSearchableSelect v-model="deliveryInfo.stockId" placeholder="Delivery Fee Stock Item" @input="lookupDeliveryFeePrice()" field="deliveryStockId" />
          </div>
          <div class="col-sm-4">
            <Spinner v-show="deliveryInfo.loadingPrice" />
            <FormInputCurrency v-show="!deliveryInfo.loadingPrice && !isDeliveryPercentageType" v-model="deliveryInfo.value" placeholder="Fixed delivery fee" />
            <FormInputText type="number" v-show="!deliveryInfo.loadingPrice && isDeliveryPercentageType" v-model="deliveryInfo.value" placeholder="Delivery levy" addon="%" />
          </div>
        </div>
        <FormRow label="Delivery Instructions">
          <FormInputText placeholder="Instructions" v-model="order.deliveryInstructions" field="deliveryInstructions" note="Additional instructions or contact info displayed on run sheets" />
        </FormRow>
        <FormHorizontalRule />
        <FormRow label="Delivery Address">
          <FormSearchableSelect
            :options="contactAddressLookup"
            v-model="order.contactAddressId"
            field="contactAddressId"
            :optionSlot="true">
            <template v-slot:option="option">
              <div class="p-1">
                <LabelTags :labelList="option.data.deliveryLabelList" class="float-right" />
                {{option.data.name}}
              </div>                          
            </template>
          </FormSearchableSelect>
        </FormRow>
        <FormRow label="Delivery Labels" v-if="addressDeliveryLabelList.length > 0">
          <LabelTags :labelList="addressDeliveryLabelList" />
        </FormRow>
        <FormRow label="Delivery Address 1">
          <FormInputAddressSearch
            placeholder="Address 1"
            v-model="order.deliveryAddress1"
            field="deliveryAddress1"
            :readonly="!isAddressEditable"
            @updateAddress2="order.deliveryAddress2 = $event"
            @updateSuburb="order.deliverySuburb = $event"
            @updateState="order.deliveryState = $event"
            @updatePostcode="order.deliveryPostcode = $event"
            @updateCountry="order.deliveryCountry = $event" />            
        </FormRow>
        <FormRow label="Delivery Address 2">
          <FormInputText placeholder="Address 2" v-model="order.deliveryAddress2" field="deliveryAddress2" :readonly="!isAddressEditable" />
        </FormRow>
        <FormRow label="Delivery Suburb">
          <FormInputText placeholder="Suburb" v-model="order.deliverySuburb" field="deliverySuburb" :readonly="!isAddressEditable" />
        </FormRow>
        <FormRow label="Delivery State">
          <FormInputText placeholder="State" v-model="order.deliveryState" field="deliveryState" :readonly="!isAddressEditable" />
        </FormRow>
        <FormRow label="Delivery Postcode">
          <FormInputText placeholder="Postcode" v-model="order.deliveryPostcode" field="deliveryPostcode" :readonly="!isAddressEditable" />
        </FormRow>
        <FormRow label="Delivery Country">
          <FormInputText placeholder="Country" v-model="order.deliveryCountry" field="deliveryCountry" :readonly="!isAddressEditable" />
        </FormRow>
      </template>
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'
import DeliveryStockSearchableSelect from '@/modules/despatch/components/deliveryStockSearchableSelect'

import { PurchaseOrderTypes, DespatchTypes, PriceTypes, UOMTypes, OrderHoldTypes } from '@/store/types'

export default {
  name: "EditOrderDespatchDialog",
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    DeliveryStockSearchableSelect
  },
  props: {
    orderId: Number
  },
  data() {
    return {
      holdProcessLoading: true,
      responseInfo: {},
      dialogForm: {},
      submitting: false,
      customerRequestedDate: false,
      deliveryInfo: {
        stockId: null,
        loadingPrice: false,
        quantity: 0,
        units: 0,
        value: 0,
        minChargePrice: 0,
        uomType: '',
        isInvoiced: false
      },
      order: {},
      customerData: {},
      accountData: {},
    }
  },
  created() {
    this.PurchaseOrderTypes = PurchaseOrderTypes
    this.DespatchTypes = DespatchTypes
    this.UOMTypes = UOMTypes
  },
  mounted() {
    
  },
  watch: {
    'order.contactAddressId': function(newVal) {
      // Fill in the delivery details
      if(this.customerData.addresses) {
        let address = this.customerData.addresses.find(address => address.id == this.order.contactAddressId)
        if(address) {
          this.order.deliveryAddress1 = address.address1
          this.order.deliveryAddress2 = address.address2
          this.order.deliverySuburb = address.suburb
          this.order.deliveryState = address.state
          this.order.deliveryPostcode = address.postcode
          this.order.deliveryCountry = address.country
        }
      }
    },
  },
  computed: {
    currentOrderDateEstimateDisplay() {
      return this.$options.filters.formatDate(this.order.dateEstimated)
    },
    isDespatchDelivery() {
      return this.order.despatchType == DespatchTypes.delivery
    },
    isDeliveryPercentageType() {
      return this.deliveryInfo.uomType == UOMTypes.percentOfLineItem || this.deliveryInfo.uomType == UOMTypes.percentOfOrder
    },
    isDeliveryItemInvoiced() {
      return this.deliveryInfo.isInvoiced
    },
    canEditRequiredDate() {
      // Can only update the required date if the user has order production approval permission
      return this.userHasUpdatePermission(this.PermissionTypes.OrderPermissionTypes.orderProductionApproval)
    },
    contactAddressLookup() {
      if(this.customerData.addresses) {
        var addressLookup = this.customerData.addresses.map(address => {
          return {
            name: address.formattedAddress,
            value: address.id,
            labelList: address.labelList,
            deliveryLabelList: address.deliveryLabelList
          }
        })
        addressLookup = _.concat(addressLookup, { name: 'Custom Address', value: null, labelList: [], deliveryLabelList: [] })
        return addressLookup
      }
      return []
    },
    isAddressEditable() {
      return this.order.contactAddressId == null
    },
    addressDeliveryLabelList() {
      if(this.order.contactAddressId == null) {
        return [{ name: 'Onsite', colour: this.$glass.orderOnsiteLabelColour }]
      }
      let addressData = this.selectedAddressData
      if(addressData && addressData.deliveryLabelList)
        return addressData.deliveryLabelList
      return []
    },
    selectedAddressData() {
      if(this.customerData.addresses) {
        if(this.customerData.addresses)
          return this.customerData.addresses.find(address => address.id == this.order.contactAddressId)
      }
      return null
    },
  },
  methods: {
    show(order) {
      // Reset form
      this.responseInfo = { }
      this.dialogForm = { }

      this.$data.order = _.cloneDeep(order)

      // Find delivery item in the order item list
      this.deliveryInfo.stockId = 0
      this.deliveryInfo.isInvoiced = false

      let orderItem = this.order.orderItems.find(item => item.isDeliveryItem)
      if(orderItem != null) {
        this.deliveryInfo.stockId = orderItem.stockId
        this.deliveryInfo.value = orderItem.unitPrice
        this.deliveryInfo.quantity = orderItem.quantity
        this.deliveryInfo.units = orderItem.units
        this.deliveryInfo.uomType = orderItem.stock ? orderItem.stock.uomType : null
        this.deliveryInfo.isInvoiced = orderItem.isInvoiced
      }

      // Get customer info for the address lookup
      this.requestCustomerDetails()

      // Force a hold on the order while editing
      // This prevents further processes from continuing (eg, despatch) while potential edits are in place
      this.$nextTick(() => {
        this.holdProcessLoading = true
        this.$apiService.ordersService.holdOrder(this.$route.params.id, { holdType: OrderHoldTypes.orderEditing, reason: 'Editing Order Despatch' })
        .then(() => {
        })
        .finally(() => {
          this.holdProcessLoading = false
        })
      })

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.submitting = true

      var updateData = {
        despatchType: this.order.despatchType,
        deliveryStockId: this.deliveryInfo.stockId,
        deliveryFeeAmount: this.deliveryInfo.value,
        contactAddressId: this.order.contactAddressId,
        deliveryAddress1: this.order.deliveryAddress1,
        deliveryAddress2: this.order.deliveryAddress2,
        deliverySuburb: this.order.deliverySuburb,
        deliveryState: this.order.deliveryState,
        deliveryPostcode: this.order.deliveryPostcode,
        deliveryCountry: this.order.deliveryCountry,
        deliveryInstructions: this.order.deliveryInstructions
      }

      updateData.deliveryTag = this.isDespatchDelivery ? this.addressDeliveryLabelList : []

      this.$apiService.ordersService.updateOrder(this.orderId, updateData)
      .then(response => {
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.$data.dialogForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.submitting = false
      })
    },
    lookupDeliveryFeePrice() {
      this.deliveryInfo.value = 0
      if(!this.deliveryInfo.stockId)
        return

      let stockLookupFilter = {
        priceType: PriceTypes.customer,
        contactId: this.$data.order.contactId,
        stockId: this.deliveryInfo.stockId
      }

      // Lookup the default delivery fee
      this.deliveryInfo.loadingPrice = true
      this.$apiService.ordersService.getContactStockLookup(stockLookupFilter)
      .then(response => {
        this.deliveryInfo.value = parseFloat(response.data.price.value)
        this.deliveryInfo.minChargePrice = parseFloat(response.data.price.minChargePrice)
        this.deliveryInfo.uomType = response.data.stock.uomType
      })
      .finally(() => {
        this.deliveryInfo.loadingPrice = false
      })
    },
    requestCustomerDetails() {
      if(this.order.contactId == null || this.order.contactId == '') {
        this.customerData = {}
        return
      }

      return this.$apiService.contactService.getContact(this.order.contactId)
      .then(result => {
        this.customerData = result.data
      })
      .catch(err => {
      })
    },
  }
}
</script>